import { ComponentsOverrides, ComponentsProps, Theme } from '@mui/material';
import { BORDER_RADIUS_XL, BORDER_RADIUS_XXXL, FontFamilies, FontWeights, TRANSITION_300 } from 'shared/config';

export const getMuiButtonOverrides = (theme: Theme): ComponentsOverrides['MuiButton'] => ({
  root: {
    padding: theme.spacing(1.5, 2),
    borderRadius: BORDER_RADIUS_XXXL,
    boxShadow: 'none',
    height: 56,
    fontSize: 16,
    lineHeight: 'auto',
    textTransform: 'none',
    fontFamily: FontFamilies.secondary,
    fontWeight: FontWeights.Bold,
    transition: TRANSITION_300,
    '&:disabled': {
      color: theme.themeColors.buttonDisabledColor,
    },
    '&:hover': {
      boxShadow: 'none',
    },
  },

  startIcon: {
    '&>*:nth-of-type(1)': {
      fontSize: 'inherit',
    },
    marginRight: theme.spacing(1.25),
  },

  endIcon: {
    '&>*:nth-of-type(1)': {
      fontSize: 'inherit',
    },
    marginLeft: theme.spacing(1.25),
  },

  contained: {
    color: theme.themeColors.buttonContainedColor,
    backgroundSize: '100%',
    position: 'relative',
    backgroundColor: theme.themeColors.buttonContainedBackgroundHover,
    zIndex: 100,
    overflow: 'hidden',
    '&:before': {
      backgroundImage: theme.themeColors.buttonContainedBackground,
      content: '""',
      display: 'block',
      height: '100%',
      position: 'absolute',
      top: 0,
      left: 0,
      opacity: 1,
      width: '100%',
      zIndex: -100,
      transition: TRANSITION_300,
    },
    '&:hover': {
      '&:before': {
        opacity: 0,
        transition: TRANSITION_300,
      },
      backgroundColor: theme.themeColors.buttonContainedBackgroundHover,
      color: theme.themeColors.buttonContainedColorHover,
    },
    '&:disabled': {
      '&:before': { display: 'none' },
      color: theme.themeColors.buttonDisabledColor,
    },
  },
  containedSecondary: {
    color: theme.themeColors.buttonContainedSecondaryColor,
    background: theme.themeColors.buttonContainedSecondaryBackground,
    '&:before': {
      display: 'none',
    },
    '&:hover': {
      background: theme.themeColors.buttonContainedSecondaryBackgroundHover,
      color: theme.themeColors.buttonContainedSecondaryColorHover,
      border: theme.themeColors.buttonContainedSecondaryBorderHover,
    },
  },
  outlined: {
    padding: theme.spacing(1, 2),
    height: 40,
    fontWeight: FontWeights.Medium,
    borderRadius: BORDER_RADIUS_XL,
    color: theme.themeColors.buttonOutlinedColor,
    background: theme.themeColors.buttonOutlinedBackground,
    border: theme.themeColors.buttonOutlinedBorder,
    '&:hover': {
      color: theme.themeColors.buttonOutlinedColorHover,
      border: theme.themeColors.buttonOutlinedBorderHover,
    },
    p: {
      pt: 0.5,
    },
  },
  text: {
    fontWeight: FontWeights.Regular,
    fontSize: 14,
    height: 'fit-content',
    color: theme.themeColors.buttonTextColor,
    border: `1px solid ${theme.themeColors.buttonTextColor}`,
    minWidth: 0,
    padding: theme.spacing(0, 1),
    '&:hover': {
      background: 'none',
    },
  },
});

export const getMuiButtonDefaultProps = (): ComponentsProps['MuiButton'] => ({
  disableElevation: false,
  disableFocusRipple: true,
  disableRipple: true,
  size: 'medium',
  variant: 'contained',
});
